/**
 * Exception Hierarchy for AutoCollect Application
 *
 * All custom exceptions extend from AppException, which provides:
 * - Consistent error structure across the application
 * - Error codes for programmatic handling
 * - User-friendly messages for display
 * - Original error preservation for debugging
 *
 * Exception Categories:
 * - VALIDATION: Input validation errors (400)
 * - AUTHENTICATION: Auth failures (401)
 * - AUTHORIZATION: Permission denied (403)
 * - NOT_FOUND: Resource not found (404)
 * - CONFLICT: Resource state conflict (409)
 * - NETWORK: Network/connectivity issues
 * - SERVER: Server-side errors (500+)
 * - UNKNOWN: Unclassified errors
 */

export enum ErrorCode {
  // Validation errors (400)
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',

  // Authentication errors (401)
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',

  // Authorization errors (403)
  AUTHORIZATION_FAILED = 'AUTHORIZATION_FAILED',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',

  // Not found errors (404)
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  CASE_NOT_FOUND = 'CASE_NOT_FOUND',
  USER_NOT_FOUND = 'USER_NOT_FOUND',

  // Conflict errors (409)
  RESOURCE_CONFLICT = 'RESOURCE_CONFLICT',
  DUPLICATE_ENTRY = 'DUPLICATE_ENTRY',

  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  CONNECTION_REFUSED = 'CONNECTION_REFUSED',

  // Server errors (500+)
  SERVER_ERROR = 'SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

  // Unknown errors
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface ExceptionDetails {
  code: ErrorCode;
  message: string;
  userMessage: string;
  statusCode?: number | undefined;
  field?: string | undefined;
  originalError?: unknown;
  metadata?: Record<string, unknown> | undefined;
}

/**
 * Base exception class for all application errors.
 *
 * @example
 * ```typescript
 * throw new AppException({
 *   code: ErrorCode.VALIDATION_ERROR,
 *   message: 'Email format is invalid',
 *   userMessage: 'Please enter a valid email address',
 *   field: 'email',
 * });
 * ```
 */
export class AppException extends Error {
  public readonly code: ErrorCode;
  public readonly userMessage: string;
  public readonly statusCode?: number | undefined;
  public readonly field?: string | undefined;
  public readonly originalError?: unknown;
  public readonly metadata?: Record<string, unknown> | undefined;
  public readonly timestamp: Date;

  constructor(details: ExceptionDetails) {
    super(details.message);
    this.name = 'AppException';
    this.code = details.code;
    this.userMessage = details.userMessage;
    this.statusCode = details.statusCode;
    this.field = details.field;
    this.originalError = details.originalError;
    this.metadata = details.metadata;
    this.timestamp = new Date();

    // Maintains proper stack trace for where error was thrown (Node only)
    const captureStackTrace = (Error as { captureStackTrace?: (target: object, constructor: Function) => void })
      .captureStackTrace;
    if (captureStackTrace) {
      captureStackTrace(this, AppException);
    }
  }

  /**
   * Check if this is a specific type of error
   */
  public isCode(code: ErrorCode): boolean {
    return this.code === code;
  }

  /**
   * Check if this is an authentication-related error
   */
  public isAuthError(): boolean {
    return [
      ErrorCode.AUTHENTICATION_FAILED,
      ErrorCode.SESSION_EXPIRED,
      ErrorCode.INVALID_CREDENTIALS,
    ].includes(this.code);
  }

  /**
   * Check if this is a validation error
   */
  public isValidationError(): boolean {
    return [
      ErrorCode.VALIDATION_ERROR,
      ErrorCode.INVALID_INPUT,
      ErrorCode.MISSING_REQUIRED_FIELD,
    ].includes(this.code);
  }

  /**
   * Check if this is a network-related error
   */
  public isNetworkError(): boolean {
    return [
      ErrorCode.NETWORK_ERROR,
      ErrorCode.TIMEOUT_ERROR,
      ErrorCode.CONNECTION_REFUSED,
    ].includes(this.code);
  }

  /**
   * Serialize exception for logging or API responses
   */
  public toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      statusCode: this.statusCode,
      field: this.field,
      metadata: this.metadata,
      timestamp: this.timestamp.toISOString(),
    };
  }
}

